import React, { useState, useRef, useEffect } from "react";
import { Clipboard, Folder, BarChart2, CheckCircle, Lock, RotateCcw } from "lucide-react";
import ReusableTable from "../dashboard/ReusableTable";
import { Dialog } from "primereact/dialog";
import ReusableForm from "../common/ReusableForm";


const TicketManagementSystem = () => {
  const cardData = [
    { title: "TOTAL TICKETS", count: 1, Icon: Clipboard, action: "View Details" },
    { title: "OPEN TICKETS", count: 1, Icon: Folder, action: "Last Updated" },
    { title: "PROGRESS TICKETS", count: 0, Icon: BarChart2, action: "Ongoing" },
    { title: "RESOLVED TICKETS", count: 3, Icon: CheckCircle, action: "Well Done!" },
    { title: "CLOSED TICKETS", count: 0, Icon: Lock, action: "No Actions Required" },
    { title: "RE-OPEN TICKETS", count: 0, Icon: RotateCcw, action: "Follow Up" },
  ];

  const [allTickets, setAllTickets] = useState([
    {
      id: 1,
      ticketNo: "T.33",
      title: "Ticket Check",
      description: "Sample ticket for checking something",
      priority: "Medium",
      status: "Open",
      createdDate: "2025-03-09",
    },
    {
      id: 2,
      ticketNo: "T.34",
      title: "Another Ticket",
      description: "Description for another ticket",
      priority: "High",
      status: "Open",
      createdDate: "2025-03-10",
    },
  ]);
  const [tickets, setTickets] = useState(allTickets);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Debounced search effect for tickets
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      if (!search) {
        setTickets(allTickets);
      } else {
        const lower = search.toLowerCase();
        setTickets(
          allTickets.filter(
            t =>
              t.ticketNo.toLowerCase().includes(lower) ||
              t.title.toLowerCase().includes(lower) ||
              t.description.toLowerCase().includes(lower) ||
              t.priority.toLowerCase().includes(lower) ||
              t.status.toLowerCase().includes(lower) ||
              t.createdDate.toLowerCase().includes(lower)
          )
        );
      }
    }, 700);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [search, allTickets]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ticketData, setTicketData] = useState({
    title: "",
    description: "",
    priority: "Medium",
    status: "Open",
  });

  const ticketFormFields = [
    {
      name: "title",
      label: "Ticket Title",
      type: "text",
      required: true,
      placeholder: "Enter ticket title",
    },
    {
      name: "description",
      label: "Description",
      type: "text",
      required: true,
      placeholder: "Enter description",
    },
    {
      name: "priority",
      label: "Priority",
      type: "select",
      required: true,
      options: [
        { label: "Low", value: "Low" },
        { label: "Medium", value: "Medium" },
        { label: "High", value: "High" },
      ],
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: true,
      options: [
        { label: "Open", value: "Open" },
        { label: "In Progress", value: "In Progress" },
        { label: "Resolved", value: "Resolved" },
        { label: "Closed", value: "Closed" },
      ],
    },
  ];

  const handleSubmitTicket = () => {
    const newTicket = {
      ...ticketData,
      id: tickets.length + 1,
      ticketNo: `T.${tickets.length + 1}`,
      createdDate: new Date().toISOString().split("T")[0],
    };
    setAllTickets((prev) => [...prev, newTicket]);
    setIsModalOpen(false);
    setTicketData({
      title: "",
      description: "",
      priority: "Medium",
      status: "Open",
    });
  };

  const columns = [
    { field: "ticketNo", header: "Ticket No", sortable: true },
    { field: "title", header: "Title", sortable: true },
    { field: "description", header: "Description", sortable: true },
    { field: "priority", header: "Priority", sortable: true },
    { field: "status", header: "Status", sortable: true },
    { field: "createdDate", header: "Created Date", sortable: true },
  ];

  return (
    <div className="bg-gray-100 p-2 sm:p-4">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-4">
          Ticket Management Dashboard
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8">
        {cardData.map((card, index) => (
          <div
            key={index}
            className="bg-gradient-to-r from-indigo-100 to-blue-100 p-3 sm:p-4 rounded-lg shadow-md flex items-center space-x-2 sm:space-x-4 hover:shadow-lg transition-shadow"
          >
            <card.Icon className="text-blue-500 flex-shrink-0" size={24} />
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-xl font-semibold text-gray-800 truncate">
                {card.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 truncate">{card.action}</p>
            </div>
            <span className="text-xl sm:text-2xl font-bold text-indigo-600">
              {card.count}
            </span>
          </div>
        ))}
      </div>

      <div className="bg-white p-2 sm:p-4 rounded shadow">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Tickets List</h2>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full sm:w-auto flex items-center justify-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
            onClick={() => setIsModalOpen(true)}
          >
            <span className="text-lg font-bold">+</span> Add Ticket
          </button>
        </div>

        <ReusableTable
          title="Tickets"
          data={tickets}
          setData={setTickets}
          columnFields={columns}
          rows={5}
          rowdelete={false}
          colorScheme={{
            headerBgColor: "#1e40af",
            headerTextColor: "#ffffff",
            tableBgColor: "#ffffff",
            tableTextColor: "#1f2937",
          }}
          searchValue={search}
          onSearchChange={handleTableSearch}
        />
      </div>

      {/* Reusable Modal Form */}
      <Dialog
        visible={isModalOpen}
        header="Add New Ticket"
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        onHide={() => setIsModalOpen(false)}
        className="rounded-xl shadow-2xl border border-gray-200 overflow-hidden max-w-lg w-full mx-auto"
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
        contentClassName="p-0"
      >
        <ReusableForm
          formData={ticketData}
          setFormData={setTicketData}
          fields={ticketFormFields}
          onSubmit={handleSubmitTicket}
          onHide={() => setIsModalOpen(false)}
        />
      </Dialog>
    </div>
  );
};

export default TicketManagementSystem;
